import React, { useEffect, useState } from "react";
import { Hourglass, Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg, fmtINR } from "../lib/api";
import { StaffLabel, staffLabelText } from "./StaffAvatar";

/** Municipal Admin sets one collector's monthly target (Org Chart → Set target). Blank / 0 clears it. */
export function CollectorTargetDialog({ code, collector, month: initialMonth, onOpenChange, onSaved }) {
  const [month, setMonth] = useState(initialMonth || new Date().toISOString().slice(0, 7));
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (collector) setMonth(initialMonth || new Date().toISOString().slice(0, 7)); }, [collector, initialMonth]);

  useEffect(() => {
    if (!collector) return;
    setInfo(null);
    api.get(`/t/${code}/targets/${collector.id}`, { params: { month } })
      .then((r) => { setInfo(r.data); setAmount(r.data.target_amount != null ? String(r.data.target_amount) : ""); })
      .catch((e) => toast.error(errMsg(e)));
  }, [code, collector, month]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put(`/t/${code}/targets/${collector.id}`, { month, target_amount: Number(amount || 0) });
      toast.success(r.data.target_amount != null
        ? `Target for ${collector.name} set to ${fmtINR(r.data.target_amount)} (${month})`
        : `Target for ${collector.name} cleared (${month})`);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!collector} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="collector-target-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <Target className="h-4 w-4" /> Monthly target — {staffLabelText(collector?.name, collector?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
              className="cursor-pointer" data-testid="collector-target-month" />
          </div>
          <div className="space-y-1">
            <Label>Target (₹)</Label>
            <Input type="number" min="0" step="100" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 50000" autoFocus data-testid="collector-target-amount" />
            <p className="text-[11px] text-muted-foreground">
              {info ? <>Collected so far in {month}: <b>{fmtINR(info.collected_mtd)}</b> ({info.receipts} receipts){info.set_by_name ? <> · current target set by <StaffLabel code={code} userId={info.set_by} name={info.set_by_name} /></> : ""}</> : "Loading…"}
              {" "}Blank or 0 clears the target.
            </p>
          </div>
          {info?.pending_proposal && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 flex items-start gap-1.5" data-testid="collector-target-pending">
              <Hourglass className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span><StaffLabel code={code} userId={info.pending_proposal.proposed_by} name={info.pending_proposal.proposed_by_name} /> proposed <b>{fmtINR(info.pending_proposal.proposed_amount)}</b> for this month.
                Saving here applies your amount and closes that proposal; to approve or reject it instead, use Approvals → Collector targets.</span>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={busy || !info} data-testid="collector-target-save">
            {busy ? "Saving..." : "Save target"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
